import React from 'react';

export interface ActivityHeatmapProps {
  data?: Array<{ date: string; count: number }>;
  isLoading?: boolean;
}

const ActivityHeatmap: React.FC<ActivityHeatmapProps> = ({ data, isLoading }) => {
  const heatmapData = React.useMemo(() => {
    if (!data || data.length === 0) {
      // Generate empty data for last 52 weeks
      const weeks: Array<{ date: string; count: number }> = [];
      const now = new Date();
      for (let i = 51; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - (i * 7));
        weeks.push({
          date: date.toISOString().split('T')[0],
          count: 0,
        });
      }
      return weeks;
    }

    // Group data by week
    const weekMap = new Map<string, number>();
    data.forEach((item) => {
      const date = new Date(item.date);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekKey = weekStart.toISOString().split('T')[0];
      weekMap.set(weekKey, (weekMap.get(weekKey) || 0) + item.count);
    });

    // Generate last 52 weeks
    const weeks: Array<{ date: string; count: number }> = [];
    const now = new Date();
    for (let i = 51; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - (i * 7));
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      const weekKey = weekStart.toISOString().split('T')[0];
      weeks.push({
        date: weekKey,
        count: weekMap.get(weekKey) || 0,
      });
    }

    return weeks;
  }, [data]);

  const maxCount = Math.max(...heatmapData.map(d => d.count), 0);

  const getIntensity = (count: number) => {
    if (count === 0) return 'bg-gray-100';
    if (maxCount === 0) return 'bg-gray-100';
    const intensity = count / maxCount;
    if (intensity < 0.25) return 'bg-green-200';
    if (intensity < 0.5) return 'bg-green-400';
    if (intensity < 0.75) return 'bg-green-600';
    return 'bg-green-800';
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Activity Heatmap</h2>
        <div className="flex items-center justify-center h-32">
          <div className="animate-pulse text-gray-400">Loading heatmap data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Activity Heatmap (Last 52 Weeks)</h2>
      <div className="flex items-start gap-1 overflow-x-auto pb-2">
        {heatmapData.map((week, index) => {
          const date = new Date(week.date);
          const weekLabel = index % 4 === 0 ? date.toLocaleDateString('en-US', { month: 'short' }) : '';
          
          return (
            <div key={index} className="flex flex-col items-center group relative">
              <div
                className={`w-3 h-8 rounded-sm transition-all hover:scale-110 cursor-pointer ${getIntensity(week.count)}`}
                title={`${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}: ${week.count} activities`}
              >
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                  {week.count} {week.count === 1 ? 'activity' : 'activities'}
                </div>
              </div>
              {weekLabel && (
                <span className="text-xs text-gray-400 mt-1">{weekLabel}</span>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Less</span>
          <div className="flex gap-1">
            <div className="w-3 h-3 rounded-sm bg-gray-100"></div>
            <div className="w-3 h-3 rounded-sm bg-green-200"></div>
            <div className="w-3 h-3 rounded-sm bg-green-400"></div>
            <div className="w-3 h-3 rounded-sm bg-green-600"></div>
            <div className="w-3 h-3 rounded-sm bg-green-800"></div>
          </div>
          <span className="text-xs text-gray-500">More</span>
        </div>
        <div className="text-xs text-gray-500">
          Total: {heatmapData.reduce((sum, w) => sum + w.count, 0)} activities
        </div>
      </div>
    </div>
  );
};

export default ActivityHeatmap;

